import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLazyQuery, gql, useReactiveVar } from '@apollo/client'
import { clsx } from 'clsx'

import { EditableComponent } from '../components'
import { apolloAuthData, getEditStatus } from '../services/apollo'
import { ROLE_MANAGER } from '../constants'
import icon1 from '../assets/icons/icon-1.svg'
import icon2 from '../assets/icons/icon-2.svg'
import icon3 from '../assets/icons/icon-3.svg'

const GET_HOME_SECTION2 = gql`
  query HomePages {
    homePages(first: 1) {
      edges {
        node {
          databaseId
          section2 {
            title
            subsections {
              title
              content
            }
          }
        }
      }
    }
  }
`

const HomeSection2 = () => {
  const auth = useReactiveVar(apolloAuthData)
  const isManager = auth.user?.capabilities?.includes(ROLE_MANAGER)
  let [searchParams] = useSearchParams()
  const isEditOn = isManager && !!searchParams.get('edit')
  const [getHomeSection2, { loading, error, data }] = useLazyQuery(
    GET_HOME_SECTION2,
    {
      fetchPolicy: 'no-cache',
    }
  )
  const homePageData = data?.homePages?.edges[0]?.node ?? null
  const section2Data = homePageData?.section2 ?? null

  useEffect(() => {
    if (!isEditOn && getEditStatus().changes.length === 0) {
      getHomeSection2()
    }
  }, [isEditOn, getHomeSection2])

  // Load data every time the page is loaded with edit mode on
  useEffect(() => {
    if (isEditOn) {
      getHomeSection2()
    }
  }, [isEditOn, getHomeSection2])

  return (
    <div className="container mb-[100px] mt-16 max-w-screen-home xl:px-0">
      {loading || !data ? (
        <></>
      ) : (
        <>
          <h1 className="mb-12 text-4xl font-bold text-skin-accent">
            {/** Title */}
            {isEditOn ? (
              <EditableComponent
                editable={isEditOn}
                doc={section2Data?.title ?? '<div></div>'}
                toolbarOption={{
                  bold: false,
                  listNumber: false,
                  listBullet: false,
                }}
                tag={{
                  page: 'HOME_PAGE',
                  type: 'section2Title',
                  databaseId: homePageData?.databaseId,
                  action: 'update',
                }}
                disableParagraph={true}
              />
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: section2Data?.title ?? '<div></div>',
                }}
              />
            )}
          </h1>
          <div className="grid grid-rows-3 place-items-start gap-16 lg:grid-cols-3 lg:grid-rows-none">
            {section2Data?.subsections?.map((subsection, index) => (
              <div key={subsection?.title ?? index} className="flex flex-col">
                <img
                  src={
                    index === 0
                      ? icon1
                      : index === 1
                      ? icon2
                      : index === 2
                      ? icon3
                      : ''
                  }
                  alt={`icon ${index + 1}`}
                  className="mx-auto h-[130px]"
                />
                <h2 className="mb-4 mt-8 text-center font-bold leading-tight">
                  {/** Subsection title */}
                  {isEditOn ? (
                    <EditableComponent
                      editable={isEditOn}
                      toolbarOption={{
                        bold: false,
                        listNumber: false,
                        listBullet: false,
                      }}
                      tag={{
                        page: 'HOME_PAGE',
                        type: 'section2SubsectionTitle',
                        index,
                        subsections: section2Data.subsections,
                        databaseId: homePageData?.databaseId,
                        action: 'update',
                      }}
                      doc={subsection?.title ?? '<div></div>'}
                    />
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: subsection?.title ?? '<div></div>',
                      }}
                    />
                  )}
                </h2>
                <div className={clsx(index < 2 && 'lg:mt-7 xl:mt-0')}>
                  {/** Subsection content */}
                  {isEditOn ? (
                    <EditableComponent
                      editable={isEditOn}
                      tag={{
                        page: 'HOME_PAGE',
                        type: 'section2SubsectionContent',
                        index,
                        subsections: section2Data.subsections,
                        databaseId: homePageData?.databaseId,
                        action: 'update',
                      }}
                      doc={subsection?.content ?? '<div></div>'}
                    />
                  ) : (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: subsection?.content ?? '<div></div>',
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default HomeSection2
